import { useSyncQueueStore } from '@/lib/stores/SyncQueueStore';
import Logging from '@/lib/Logging';
import { EventBus, Events } from '@/lib/EventBus';

interface QueuedRequest {
    execute: () => Promise<any>;
    resolve: (value: any) => void;
    reject: (reason: any) => void;
}

export class ChangeSetQueue {
    // First item in each array is always the in-flight request; remaining items are queued.
    private queues = new Map<string, QueuedRequest[]>();

    public enqueue<T>(locationId: string, fn: () => Promise<T>): Promise<T> {
        const store = useSyncQueueStore();
        store.increment(locationId);

        return new Promise<T>((resolve, reject) => {
            const request: QueuedRequest = {
                execute: fn,
                resolve,
                reject,
            };

            const queue = this.queues.get(locationId);
            if (queue) {
                queue.push(request);
            } else {
                this.queues.set(locationId, [request]);
                this.executeNext(locationId);
            }
        });
    }

    public get pendingCount(): number {
        return useSyncQueueStore().totalPendingCount;
    }

    public get hasPendingSyncs(): boolean {
        return useSyncQueueStore().hasPendingSyncs;
    }

    private executeNext(locationId: string): void {
        const store = useSyncQueueStore();
        const queue = this.queues.get(locationId);
        if (!queue || queue.length === 0) {
            this.queues.delete(locationId);
            return;
        }

        const request = queue[0];

        request.execute()
            .then((result) => {
                store.decrement(locationId);
                request.resolve(result);
                queue.shift();
                if (queue.length === 0) {
                    this.queues.delete(locationId);
                } else {
                    this.executeNext(locationId);
                }
            })
            .catch((error) => {
                store.decrement(locationId);
                request.reject(error);
                queue.shift();
                this.discardQueue(locationId, error);
            });
    }

    private discardQueue(locationId: string, error: any): void {
        const store = useSyncQueueStore();
        const queue = this.queues.get(locationId);

        if (queue && queue.length > 0) {
            const discardedCount = queue.length;

            for (const item of queue) {
                store.decrement(locationId);
                item.reject(error);
            }

            Logging.warning(`Discarded ${discardedCount} queued sync request(s) for location ${locationId} due to a failed request.`);
            EventBus.$emit(
                Events.ToastWarning,
                'A sync request failed. Pending changes for this location were discarded. Please refresh and check your data.',
                true
            );
        }

        this.queues.delete(locationId);
        store.clearLocation(locationId);
    }
}

export default new ChangeSetQueue();
